<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-flag-checkered</v-icon>
        <span class="px-2">{{ $t('get_started') }}</span>
        <v-spacer></v-spacer>
        <v-chip v-if="!isLoading" :color="allDone ? 'success' : 'primary'" dark>
          {{ $t('steps_done', { done: doneCount, total: visibleSteps.length }) }}
        </v-chip>
      </v-card-title>

      <v-card-subtitle>{{ $t('get_started_intro') }}</v-card-subtitle>

      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-card-text v-else>
        <v-alert v-if="allDone" type="success" text class="mb-4">
          {{ $t('get_started_all_done') }}
        </v-alert>

        <v-list two-line>
          <template v-for="(step, index) in visibleSteps">
            <v-list-item :key="step.key" @click="go(step)">
              <v-list-item-icon>
                <!-- a step whose groundwork is missing is shown as waiting
                     rather than as something to do now, so the order the panel
                     actually needs is visible on the page -->
                <v-icon :color="stepColor(step)">{{ stepIcon(step) }}</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  <strong>{{ index + 1 }}. {{ $t(step.title) }}</strong>
                  <v-chip x-small class="ms-2" :color="stepColor(step)" dark>
                    {{ stepState(step) }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t(step.what) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="step.blockedBy && !step.blockedBy(progress)" class="text-wrap warning--text">
                  {{ $t(step.blockedText) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else-if="stepCount(step)" class="text-wrap">
                  {{ stepCount(step) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn small text color="primary">
                  {{ step.done(progress) ? $t('view') : $t('open') }}
                  <v-icon small right>mdi-chevron-right</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index < visibleSteps.length - 1" :key="step.key + '-d'"></v-divider>
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
/*
 * What to do, in the order the panel needs it done.
 *
 * The dependencies between these screens are real and invisible: a route is a
 * list of stops, so stops come first; a trip needs a route and a driver; a
 * child can only be put on a bus or a trip once one exists. A school setting
 * itself up in the wrong order reaches a screen it cannot use and is given no
 * reason why. Every line here is read from the school's own data rather than
 * ticked off by hand, so it cannot drift out of date.
 */
export default {
  name: "GettingStarted",
  data() {
    return {
      isLoading: false,
      progress: {
        simple_mode: false,
        settings: { done: false },
        stops: 0,
        routes: 0,
        buses: 0,
        drivers: 0,
        drivers_with_a_bus: 0,
        trips: 0,
        trips_with_a_driver: 0,
        students: 0,
        students_mapped: 0,
      },
      steps: [
        {
          key: "settings",
          title: "step_school_title",
          what: "step_school_what",
          route: "school",
          done: (p) => p.settings.done,
          simpleModeOnly: true,
        },
        {
          key: "stops",
          title: "step_stops_title",
          what: "step_stops_what",
          route: "stops",
          done: (p) => p.stops >= 2,
          count: (p) => ({ key: "step_stops_count", args: { count: p.stops } }),
        },
        {
          key: "routes",
          title: "step_routes_title",
          what: "step_routes_what",
          route: "routes",
          done: (p) => p.routes > 0,
          blockedBy: (p) => p.stops >= 2,
          blockedText: "step_routes_blocked",
          count: (p) => ({ key: "step_routes_count", args: { count: p.routes } }),
        },
        {
          key: "buses",
          title: "step_buses_title",
          what: "step_buses_what",
          route: "buses",
          done: (p) => p.buses > 0,
          count: (p) => ({ key: "step_buses_count", args: { count: p.buses } }),
        },
        {
          key: "drivers",
          title: "step_drivers_title",
          what: "step_drivers_what",
          route: "drivers",
          done: (p) => p.drivers_with_a_bus > 0,
          blockedBy: (p) => p.buses > 0,
          blockedText: "step_drivers_blocked",
          count: (p) => ({
            key: "step_drivers_count",
            args: { count: p.drivers, onBus: p.drivers_with_a_bus },
          }),
        },
        {
          key: "trips",
          title: "step_trips_title",
          what: "step_trips_what",
          route: "trips",
          done: (p) => p.trips_with_a_driver > 0,
          blockedBy: (p) => p.routes > 0 && p.drivers_with_a_bus > 0,
          blockedText: "step_trips_blocked",
          count: (p) => ({
            key: "step_trips_count",
            args: { count: p.trips, withDriver: p.trips_with_a_driver },
          }),
        },
        {
          key: "students",
          title: "step_students_title",
          what: "step_students_what",
          route: "students",
          done: (p) => p.students > 0 && p.students_mapped > 0,
          blockedBy: (p) => p.buses > 0 || p.trips > 0,
          blockedText: "step_students_blocked",
          count: (p) => ({
            key: "step_students_count",
            args: { count: p.students, mapped: p.students_mapped },
          }),
        },
      ],
    };
  },
  computed: {
    //the school address and the days the school runs are simple-mode
    //settings: in advanced mode the form holding them is hidden, and nothing
    //reads them either. Listing the step anyway asked for something that
    //could not be entered, and left the count one short for good
    visibleSteps() {
      return this.steps.filter(
        (step) => !step.simpleModeOnly || this.progress.simple_mode
      );
    },
    doneCount() {
      return this.visibleSteps.filter((step) => step.done(this.progress)).length;
    },
    allDone() {
      return this.doneCount === this.visibleSteps.length;
    },
  },
  mounted() {
    this.loadProgress();
  },
  methods: {
    loadProgress() {
      this.isLoading = true;
      axios
        .get("/school-dashboard/setup-progress")
        .then((response) => {
          this.progress = response.data;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_setup_progress"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    stepColor(step) {
      if (step.done(this.progress)) return "success";
      if (step.blockedBy && !step.blockedBy(this.progress)) return "warning";
      return "primary";
    },
    stepIcon(step) {
      if (step.done(this.progress)) return "mdi-check-circle";
      if (step.blockedBy && !step.blockedBy(this.progress)) return "mdi-lock-clock";
      return "mdi-circle-outline";
    },
    stepState(step) {
      if (step.done(this.progress)) return this.$t("step_done");
      if (step.blockedBy && !step.blockedBy(this.progress)) return this.$t("step_waiting");
      return this.$t("step_to_do");
    },
    stepCount(step) {
      if (!step.count) return null;
      const line = step.count(this.progress);
      return this.$t(line.key, line.args);
    },
    go(step) {
      this.$router.push({ name: step.route });
    },
  },
};
</script>
